import React, { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Form,
  Input,
  Label,
  Row,
  Col,
  Table,
} from "reactstrap";
import PopupModal from "../../components/Common/PopupModal";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import api from "../../api";

const AdminDashboard = () => {
  const [open, setOpen] = useState("1");
  const [bannedUsers, setBannedUsers] = useState([]); // Placeholder
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    backgroundColor: "#1d222e",
  });
  const [usernameToModify, setUsernameToModify] = useState("");
  const [banReason, setBanReason] = useState("");
  const [banEnds, setBanEnds] = useState("");
  const [category, setCategory] = useState("");
  const [availablePokemon, setAvailablePokemon] = useState([]);
  const [selectedPokemon, setSelectedPokemon] = useState("");
  const [gender, setGender] = useState("U");
  const [amount, setAmount] = useState(1);
  const [targetUsername, setTargetUsername] = useState("");
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemAmount, setItemAmount] = useState(1);
  const [itemTargetUser, setItemTargetUser] = useState("");
  const handlePopup = useCallback((message, backgroundColor = "#1d222e") => {
    setPopup({ isOpen: true, message, backgroundColor });

    // Automatically close the popup after 2 seconds
    setTimeout(() => {
      setPopup((prev) => ({ ...prev, isOpen: false }));
    }, 2000);
  }, []);
  
  const fetchBannedUsers = async () => {
    try {
      const res = await api.get("/v1/admin/banned-users", {
        withCredentials: true,
      });
      setBannedUsers(res.data.banned_users || []);
    } catch (err) {
      console.error("Failed to fetch banned users:", err);
    }
  };

  const handleAddItem = async () => {
    if (!selectedItem || !itemTargetUser) {
      handlePopup("Please select an item and enter a username.", "red");
      return;
    }

    const payload = {
      target_username: itemTargetUser,
      item_id: selectedItem.id,
      amount: itemAmount,
    };

    try {
      const res = await api.post("/v1/admin/add-item", payload, {
        withCredentials: true,
      });
      handlePopup(res.data.message, "#1d222e");

      // Reset form
      setSelectedItem(null);
      setItemTargetUser("");
      setItemAmount(1);
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to add item.", "red");
    }
  };

  const fetchItems = async () => {
    try {
      const res = await api.get("/v1/admin/available-data?type=item", {
        withCredentials: true,
      });
      setAvailableItems(res.data.results || []);
    } catch (err) {
      console.error("Failed to fetch items:", err);
    }
  };

  const handleBan = async () => {
    try {
      const payload = {
        target_username: usernameToModify,
        action: "ban",
        reason: banReason || "No reason provided",
        ends: banEnds ? Math.floor(new Date(banEnds).getTime() / 1000) : 0,
      };
      const res = await api.post("/v1/admin/ban-unban-user", payload, {
        withCredentials: true,
      });
      handlePopup(res.data.message, "red");
      fetchBannedUsers();
      setUsernameToModify("");
      setBanReason("");
      setBanEnds("");
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to ban user", "red");
    }
  };

  const handleUnban = async () => {
    try {
      const payload = {
        target_username: usernameToModify,
        action: "unban",
      };
      const res = await api.post("/v1/admin/ban-unban-user", payload, {
        withCredentials: true,
      });
      handlePopup(res.data.message, "#1d222e");
      fetchBannedUsers(); // Refresh list
    } catch (err) {
      handlePopup(err.response?.data?.detail || "Failed to unban user", "red");
    }
  };

  useEffect(() => {
    fetchBannedUsers();
  }, []);

  const handleAccordionToggle = (id) => {
    setOpen(open === id ? "" : id);
  };

  const emailSchema = Yup.object({
    email: Yup.string()
      .email("Must be a valid email")
      .max(255)
      .required("Please Enter Your Email"),
    username: Yup.string()
      .min(4, "Minimum of 4 characters")
      .max(20, "Maximum of 20 characters")
      .required("Username is required"),
  });

  const passwordSchema = Yup.object({
    password: Yup.string()
      .min(4, "Minimum of 7 characters")
      .max(20, "Maximum of 20 characters")
      .matches(
        /(?=.*[!@#$%^&*()\-_=+{};:,<.>])/,
        "Password must contain at least one special character",
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password"),
    username: Yup.string()
      .min(4, "Minimum of 4 characters")
      .max(20, "Maximum of 20 characters")
      .required("Username is required"),
  });

  // handlePopup is now defined at the top of the component

  const updateEmail = async (values, actions) => {
    try {
      const payload = {
        target_username: values.username,
        new_email: values.email,
      };
      const res = await api.put("/v1/admin/update-email", payload, {
        withCredentials: true,
      });
      handlePopup(res.data.message, "#1d222e");
      actions.resetForm();
    } catch (err) {
      console.error(err);
      handlePopup(
        err.response?.data.detail || "Failed to update email.",
        "red",
      );
    }
  };

  const handleAddPokemon = async () => {
    if (!selectedPokemon || !targetUsername) {
      handlePopup("Please select a Pokémon and enter a username.", "red");
      return;
    }

    const normalizedGender = (gender || "U").toString().toUpperCase();

    const payload = {
      username: targetUsername,
      pokemon_name: selectedPokemon,
      gender: normalizedGender,
      amount,
    };

    try {
      const res = await api.post("/v1/admin/add-pokemon", payload, {
        withCredentials: true,
      });
      handlePopup(res.data.message, "#1d222e");

      // Reset form
      setSelectedPokemon("");
      setGender("U");
      setAmount(1);
      setTargetUsername("");
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to add Pokémon.", "red");
    }
  };

  const handleCategoryChange = async (e) => {
    const selectedCategory = e.target.value;
    setCategory(selectedCategory);

    try {
      const res = await api.get(`/v1/admin/available-data`, {
        params: {
          type: "pokemon",
          category: selectedCategory,
        },
        withCredentials: true,
      });

      const pokemonList = res.data.results;

      // Check for which Pokémon actually have a sprite file
      const checkImageExists = async (name) => {
        try {
          const imgRes = await fetch(`/sprites/${name}.png`, {
            method: "HEAD",
          });
          return imgRes.ok;
        } catch {
          return false;
        }
      };

      const filteredWithSprites = [];
      for (const p of pokemonList) {
        const hasSprite = await checkImageExists(p.name);
        if (hasSprite) filteredWithSprites.push(p);
      }

      setAvailablePokemon(filteredWithSprites);
    } catch (err) {
      console.error("Failed to fetch Pokémon or check sprites:", err);
    }
  };

  const updatePassword = async (values, actions) => {
    try {
      const payload = {
        target_username: values.username,
        new_password: values.password,
      };
      const res = await api.put("/v1/admin/update-password", payload, {
        withCredentials: true,
      });
      handlePopup(res.data.message, "#1d222e");
      actions.resetForm();
    } catch (err) {
      console.error(err);
      handlePopup(
        err.response?.data.detail || "Failed to update password.",
        "red",
      );
    }
  };

  return (
        <div className="page-content">
      <Accordion open={open} toggle={handleAccordionToggle}>
        {/* 1. Update Information */}
        {/* <AccordionItem>
          <AccordionHeader targetId="1">
            Update User Information
          </AccordionHeader>
          <AccordionBody accordionId="1">
            <h5>Update Email</h5>
            <Formik
              initialValues={{ username: "", email: "" }}
              validationSchema={emailSchema}
              onSubmit={updateEmail}
            >
              <FormikForm>
                <Row className="mb-3">
                  <Label className="col-md-2 col-form-label">Username</Label>
                  <Col md="10">
                    <Field
                      name="username"
                      className="form-control"
                      placeholder="Enter username"
                    />
                    <ErrorMessage
                      name="username"
                      component="div"
                      className="text-danger"
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Label className="col-md-2 col-form-label">New Email</Label>
                  <Col md="10">
                    <Field
                      name="email"
                      type="email"
                      className="form-control"
                      placeholder="Enter new email"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger"
                    />
                  </Col>
                </Row>
                <Button color="primary" type="submit">
                  Update Email
                </Button>
              </FormikForm>
            </Formik>

            <hr />

            <h5>Update Password</h5>
            <Formik
              initialValues={{
                username: "",
                password: "",
                confirmPassword: "",
              }}
              validationSchema={passwordSchema}
              onSubmit={updatePassword}
            >
              <FormikForm>
                <Row className="mb-3">
                  <Label className="col-md-2 col-form-label">Username</Label>
                  <Col md="10">
                    <Field
                      name="username"
                      className="form-control"
                      placeholder="Enter username"
                    />
                    <ErrorMessage
                      name="username"
                      component="div"
                      className="text-danger"
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Label className="col-md-2 col-form-label">
                    New Password
                  </Label>
                  <Col md="10">
                    <Field
                      name="password"
                      type="password"
                      className="form-control"
                      placeholder="Enter new password"
                    />
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-danger"
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Label className="col-md-2 col-form-label">
                    Confirm Password
                  </Label>
                  <Col md="10">
                    <Field
                      name="confirmPassword"
                      type="password"
                      className="form-control"
                      placeholder="Confirm password"
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="text-danger"
                    />
                  </Col>
                </Row>
                <Button color="primary" type="submit">
                  Update Password
                </Button>
              </FormikForm>
            </Formik>
          </AccordionBody>
        </AccordionItem> */}

        {/* 2. Ban/Unban Player */}
        <AccordionItem>
          <AccordionHeader targetId="2">Ban/Unban Player</AccordionHeader>
          <AccordionBody accordionId="2">
            <Form inline>
              <Row className="mb-3">
                <Col md="5">
                  <Input
                    type="text"
                    placeholder="Enter username"
                    value={usernameToModify}
                    onChange={(e) => setUsernameToModify(e.target.value)}
                    className="mb-2"
                  />
                  <Input
                    type="text"
                    placeholder="Reason (for ban)"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    className="mb-2"
                  />
                  <Input
                    type="datetime-local"
                    placeholder="Ban ends (optional)"
                    value={banEnds}
                    onChange={(e) => setBanEnds(e.target.value)}
                  />
                </Col>
                <Col md="3">
                  <Button color="danger" onClick={handleBan}>
                    Ban
                  </Button>{" "}
                  <Button color="success" onClick={handleUnban}>
                    Unban
                  </Button>
                </Col>
              </Row>
            </Form>

            <h5 className="mt-4">Currently Banned Players</h5>
            <Table striped>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>Reason</th>
                  <th>Ends</th>
                </tr>
              </thead>
              <tbody>
                {bannedUsers.map((user, idx) => (
                  <tr key={user.username}>
                    <td>{idx + 1}</td>
                    <td>{user.username}</td>
                    <td>{user.reason}</td>
                    <td>{user.end_time || "Indefinite"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </AccordionBody>
        </AccordionItem>

        {/* 3. Add Pokémon */}
        <AccordionItem>
          <AccordionHeader targetId="3">Add Pokémon</AccordionHeader>
          <AccordionBody accordionId="3">
            <Form>
   

              <Row className="mb-3">
  <Label className="col-md-2 col-form-label">Pokémon</Label>
  <Col md="10">
    {/* 1) Simple text input */}
    <Input
      type="text"
      placeholder="Enter Pokémon name"
      value={selectedPokemon}
      onChange={(e) => setSelectedPokemon(e.target.value)}
    />

    {/* 2) (Optional) add HTML5 datalist for suggestions */}
    <datalist id="pokemon-list">
      {availablePokemon.map((p) => (
        <option key={p.name} value={p.name} />
      ))}
    </datalist>
    {/* To use the datalist above, swap the Input to: */}
    {/*
    <Input
      type="text"
      list="pokemon-list"
      placeholder="Enter Pokémon name"
      value={selectedPokemon}
      onChange={(e) => setSelectedPokemon(e.target.value)}
    />
    */}
  </Col>
</Row>


              {selectedItem?.image && (
                <div className="text-center mb-3">
                  <img
                    src={`/${selectedItem.image}`}
                    alt={selectedItem.name}
                    style={{ height: "80px" }}
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
              )}

              <Row className="mb-3">
                <Label className="col-md-2 col-form-label">Gender</Label>
                <Col md="10">
                  <Input
                    type="select"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="U">Ungendered</option>
                  </Input>
                </Col>
              </Row>

              <Row className="mb-3">
                <Label className="col-md-2 col-form-label">Amount</Label>
                <Col md="10">
                  <Input
                    type="number"
                    min="1"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Label className="col-md-2 col-form-label">User</Label>
                <Col md="10">
                  <Input
                    type="text"
                    value={targetUsername}
                    onChange={(e) => setTargetUsername(e.target.value)}
                    placeholder="Username to receive Pokémon"
                  />
                </Col>
              </Row>

              <Button color="success" onClick={handleAddPokemon}>
                Add Pokémon
              </Button>
            </Form>
          </AccordionBody>
        </AccordionItem>
        <AccordionItem>
          <AccordionHeader targetId="4">Add Item</AccordionHeader>
          <AccordionBody accordionId="4">
            <Form>
              <Row className="mb-3">
                <Label className="col-md-2 col-form-label">Item</Label>
                <Col md="10">
                  <Input
                    type="select"
                    onClick={fetchItems}
                    value={selectedItem?.id || ""}
                    onChange={(e) => {
                      const item = availableItems.find(
                        (i) => i.id === Number(e.target.value),
                      );
                      setSelectedItem(item || null);
                    }}
                  >
                    <option value="">-- Select Item --</option>
                    {availableItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              {selectedItem?.image && (
                <div className="text-center mb-3">
                  <img
                    src={`/${selectedItem.image}`}
                    alt={selectedItem.name}
                    style={{ height: "30px" }}
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
              )}

              <Row className="mb-3">
                <Label className="col-md-2 col-form-label">Amount</Label>
                <Col md="10">
                  <Input
                    type="number"
                    min="1"
                    value={itemAmount}
                    onChange={(e) => setItemAmount(Number(e.target.value))}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Label className="col-md-2 col-form-label">User</Label>
                <Col md="10">
                  <Input
                    type="text"
                    placeholder="Username to receive item"
                    value={itemTargetUser}
                    onChange={(e) => setItemTargetUser(e.target.value)}
                  />
                </Col>
              </Row>

              <Button color="success" onClick={handleAddItem}>
                Add Item
              </Button>
            </Form>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
      <PopupModal
        isOpen={popup.isOpen}
        message={popup.message}
        backgroundColor={popup.backgroundColor}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />
    </div>
  );
};

export default AdminDashboard;
